import { useState, useEffect, useCallback, useRef, type FC } from "react";
import { useNavigate } from "react-router-dom";

import { supabase } from "@/shared/lib/supabase";
import { LAWYER_ROUTES } from "@/shared/constants/paths";

import { AttorneyAuthLayout } from "./components/AttorneyAuthLayout";
import { VerifyEmailStep } from "./components/VerifyEmailStep";
import { useLawyerSignup } from "./LawyerSignupContext";

const LawyerVerifyPage: FC = () => {
  const navigate = useNavigate();
  const { email: contextEmail, password } = useLawyerSignup();
  const email = contextEmail || sessionStorage.getItem("lawyer_signup_email") || "";
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const verifyingRef = useRef(false);

  useEffect(() => {
    if (!email) {
      navigate(LAWYER_ROUTES.SIGNUP, { replace: true });
    }
  }, [email, navigate]);

  const handleVerify = useCallback(
    async (code: string) => {
      if (verifyingRef.current || !email) return;
      verifyingRef.current = true;
      setLoading(true);
      setError(null);

      try {
        const { data: verifyData, error: verifyError } = await supabase.auth.verifyOtp({
          email,
          token: code,
          type: "signup",
        });

        if (verifyError || !verifyData.user) {
          setError("Invalid code. Please try again.");
          verifyingRef.current = false;
          return;
        }

        navigate(LAWYER_ROUTES.SIGNUP_PROFILE, { replace: true });
      } catch {
        setError("An unexpected error occurred");
        verifyingRef.current = false;
      } finally {
        setLoading(false);
      }
    },
    [email, navigate],
  );

  const handleResend = useCallback(async (): Promise<boolean> => {
    if (!email) return false;
    if (!password) {
      navigate(LAWYER_ROUTES.SIGNUP, { replace: true });
      return false;
    }
    setError(null);
    setLoading(true);

    try {
      const { data, error: invokeError } = await supabase.functions.invoke(
        "communications/verification-code/send",
        { body: { email, password } },
      );

      if (invokeError || data?.error) {
        setError("Failed to resend code. Please try again.");
        return false;
      }

      return true;
    } catch {
      setError("An unexpected error occurred");
      return false;
    } finally {
      setLoading(false);
    }
  }, [email, password, navigate]);

  const handleChangeEmail = useCallback(() => {
    navigate(LAWYER_ROUTES.SIGNUP, { replace: true });
  }, [navigate]);

  if (!email) {
    return null;
  }

  return (
    <AttorneyAuthLayout>
      <VerifyEmailStep
        email={email}
        onVerify={handleVerify}
        onResend={handleResend}
        onChangeEmail={handleChangeEmail}
        isLoading={loading}
        error={error}
      />
    </AttorneyAuthLayout>
  );
};

export default LawyerVerifyPage;
